import { apiRequest, createUrlWithCenterId } from '../utils/apiHelpers';
import { LIST_PAGE_SIZE, PagedResult, normalizePagedResult } from '../utils/listSearch';

export interface Puesto {
  id: number;
  sucursalId: number;
  sucursal?: {
    id: number;
    nombre: string;
  };
  extintorId?: number;
  extintor?: {
    id: number;
    codigo?: string;
    nroSerie?: string;
    tipoDeCarga?: {
      id: number;
      nombre: string;
    };
    capacidad?: {
      id: number;
      valor: string;
      unidad?: string;
    };
    reserva?: boolean;
    baja?: boolean;
    vencimientoCarga?: string;
    vencimientoPH?: string;
  };
  nombre?: string;
  ubicacion?: string;
  codigo?: string;
  deshabilitado: boolean;
  fechaDeshabilitacion?: string;
  activo: boolean;
  ultimoRelevamiento?: string;
}

export const puestoService = {
  // Obtener todos los puestos
  getAll: async (filters?: {
    q?: string;
    clienteId?: number;
    sucursalId?: number;
    estado?: string;
  }): Promise<Puesto[]> => {
    const endpoint = createUrlWithCenterId('/api/Puestos', filters);
    return await apiRequest(endpoint);
  },

  list: async (
    filters: {
      q?: string;
      clienteId?: number;
      sucursalId?: number;
      estado?: string;
      page?: number;
      pageSize?: number;
    },
    signal?: AbortSignal,
  ): Promise<PagedResult<Puesto>> => {
    const endpoint = createUrlWithCenterId('/api/Puestos', {
      ...filters,
      page: filters.page ?? 1,
      pageSize: filters.pageSize ?? LIST_PAGE_SIZE,
    });
    return normalizePagedResult(await apiRequest(endpoint, { signal }));
  },

  // Obtener puesto por ID
  getById: async (id: number): Promise<Puesto> => {
    const endpoint = createUrlWithCenterId(`/api/Puestos/${id}`);
    return await apiRequest(endpoint);
  },

  // Obtener puestos por sucursal
  getBySucursal: async (sucursalId: number): Promise<Puesto[]> => {
    const endpoint = createUrlWithCenterId('/api/Puestos/sucursal', {
      sucursalId: sucursalId.toString(),
    });
    return await apiRequest(endpoint);
  },

  // Obtener puestos por extintor
  getByExtintor: async (extintorId: number): Promise<Puesto[]> => {
    const endpoint = createUrlWithCenterId(`/api/Puestos/extintor/${extintorId}`);
    return await apiRequest(endpoint);
  },

  // Crear puesto
  create: async (puesto: Omit<Puesto, 'id'>): Promise<Puesto> => {
    const endpoint = createUrlWithCenterId('/api/Puestos');
    return await apiRequest(endpoint, {
      method: 'POST',
      body: JSON.stringify(puesto),
    });
  },

  // Actualizar puesto
  update: async (id: number, puesto: Partial<Puesto>): Promise<void> => {
    const endpoint = createUrlWithCenterId(`/api/Puestos/${id}`);
    await apiRequest(endpoint, {
      method: 'PUT',
      body: JSON.stringify(puesto),
    });
  },

  // Eliminar puesto (borrado lógico)
  delete: async (id: number): Promise<{ message: string }> => {
    const endpoint = createUrlWithCenterId(`/api/Puestos/${id}`);
    return await apiRequest(endpoint, {
      method: 'DELETE',
    });
  },
};
